import { map } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs/internal/Observable';
import { environment } from '../../../environments/environment';
import { Company, FieldActivityGrouped } from '../_models';
import { StoreModelService } from './storeModel.service';
import { SM } from '../../../assets/data/enum/storeModel.enum';
import { StoreModelAction } from '../_models/storeModelAction';
import { UiMode } from '../../../assets/data/enum/uiMode';

export type DataCultivationPlanPayload = {
  year: number;
  fields: Array<number>;
  fileName: string;
};

export type DataExportFuncProps = {
  type: string;
  subtype: string;
  payload: DataCultivationPlanPayload;
  format: string;
  compression: boolean;
};

@Injectable()
export class ExportService {
  private apiBaseUrl = environment.apiBaseUrl;
  private checkActionExportBio: StoreModelAction = new StoreModelAction();
  private checkActionExportGlobalGap: StoreModelAction = new StoreModelAction();
  private checkActionActivity: StoreModelAction = new StoreModelAction();
  private checkActionSensor: StoreModelAction = new StoreModelAction();
  private checkActionWarehouse: StoreModelAction = new StoreModelAction();
  private checkActionSilos: StoreModelAction = new StoreModelAction();
  private checkActionFinance: StoreModelAction = new StoreModelAction();
  private checkActionRations: StoreModelAction = new StoreModelAction();
  private checkActionTelemetry: StoreModelAction = new StoreModelAction();
  UiMode = UiMode;
  headers = new HttpHeaders();

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private storeModelService: StoreModelService
  ) {
    this.checkActionExportBio = this.storeModelService.enableActionByStoreModelAndPermission(
      SM.SM_EXPORT_BIO
    );
    this.checkActionExportGlobalGap = this.storeModelService.enableActionByStoreModelAndPermission(
      SM.SM_EXPORT_GLOBAL_GAP
    );
    this.checkActionActivity = this.storeModelService.enableActionByStoreModelAndPermission(
      SM.SM_FIELDACTIVITY
    );
    this.checkActionSensor = this.storeModelService.enableActionByStoreModelAndPermission(
      SM.SM_SENSOR
    );
    this.checkActionWarehouse = this.storeModelService.enableActionByStoreModelAndPermission(
      SM.SM_WAREHOUSE
    );
    this.checkActionSilos = this.storeModelService.enableActionByStoreModelAndPermission(
      SM.SM_SILOS
    );
    this.checkActionFinance = this.storeModelService.enableActionByStoreModelAndPermission(
      SM.SM_FINANCE
    );
    this.checkActionRations = this.storeModelService.enableActionByStoreModelAndPermission(
      SM.SM_RATIONS
    );
    this.checkActionTelemetry = this.storeModelService.enableActionByStoreModelAndPermission(
      SM.SM_EQUIPMENT_TELEMETRY
    );
  }

  getData(
    type: string,
    subtype: string,
    payload: any,
    format: string,
    compression: boolean
  ): Observable<Blob> {
    let version = 'v1';
    if (type === 'Devices') {
      version = 'v2';
    }

    return this.http
      .post(
        this.apiBaseUrl +
          'api/private/' +
          version +
          '/export/' +
          type +
          '?type=' +
          type +
          '&subtype=' +
          subtype +
          '&format=' +
          format.toLowerCase() +
          '&compression=' +
          compression,
        JSON.stringify(payload),
        { headers: this.headers, responseType: 'blob' }
      )
      .pipe(
        map((response: Blob) => {
          return response;
        })
      );
  }

  getDataCultivationPlan({ type, subtype, payload, format, compression }: DataExportFuncProps) {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const version = 'v2';

    return this.http.post(
      this.apiBaseUrl +
        'api/private/' +
        version +
        '/export/doc/cultivationPlan' +
        '?type=' +
        type +
        '&subtype=' +
        subtype +
        '&format=' +
        format.toLowerCase() +
        '&compression=' +
        compression,
      JSON.stringify(payload),
      { headers, responseType: 'blob' }
    );
  }

  getDataFieldActivities(
    type: string,
    subtype: string,
    payload: {
      fields?: number[];
      types?: string[];
      users?: number[];
      commodities?: number[];
      year?: number;
    },
    format: string,
    compression: boolean
  ): Observable<Blob> {
    const version = 'v1'; // Version appears to be constant in this method

    return this.http
      .post(
        `${this.apiBaseUrl}api/private/${version}/export/fieldActivityList?type=${type}&subtype=${subtype}&format=${format.toLowerCase()}&compression=${compression}`,
        JSON.stringify(payload),
        { headers: this.headers, responseType: 'blob' }
      )
      .pipe(
        map((response: Blob) => {
          return response;
        })
      );
  }

  exportWorkPlanFieldActivities(
    grouped: boolean = false,
    payload: {
      fileName: string;
      year: number;
      fields: number[];
      types: string[];
      users: number[];
      commodities: number[];
      tractors: number[];
      equipments: number[];
      phenologicalStages: string[];
      done: boolean;
    }
  ): Observable<Blob> {
    return this.http
      .post(
        `${this.apiBaseUrl}api/private/v2/export/doc/workPlan?format=xlsx&grouped=${grouped}`,
        payload,
        {
          headers: this.headers,
          responseType: 'blob',
        }
      )
      .pipe(
        map((response: Blob) => {
          return response;
        })
      );
  }

  getMeasurementExport(insectZoneId: number, startDate: Date, endDate: Date): Observable<Blob> {
    // Convert dates into ISO format request by the export
    const encodedStartDate = encodeURIComponent(startDate.toISOString());
    const encodedEndDate = encodeURIComponent(endDate.toISOString());

    const url = `${this.apiBaseUrl}api/private/v2/insects/measurements/export?insectZoneId=${insectZoneId}&startDate=${encodedStartDate}&endDate=${encodedEndDate}`;

    return this.http.get(url, { headers: this.headers, responseType: 'blob' });
  }

  getDataGroupedFieldActivities(
    type: string,
    subtype: string,
    payload: any,
    format: string,
    compression: boolean
  ): Observable<Blob> {
    const version = 'v1'; // Version appears to be constant in this method

    return this.http
      .post(
        `${this.apiBaseUrl}api/private/${version}/export/groupedFieldActivityList?type=${type}&subtype=${subtype}&format=${format.toLowerCase()}&compression=${compression}`,
        JSON.stringify(payload),
        { headers: this.headers, responseType: 'blob' }
      )
      .pipe(
        map((response: Blob) => {
          return response;
        })
      );
  }

  getDataSilos(
    type: string,
    subtype: string,
    payload: any,
    format: string,
    compression: boolean
  ): Observable<Blob> {
    return this.http
      .post(
        `${this.apiBaseUrl}api/private/v1/export/${type}?type=${subtype}&subtype=${subtype}&format=${format.toLowerCase()}&compression=${compression}`,
        JSON.stringify(payload),
        { headers: this.headers, responseType: 'blob' }
      )
      .pipe(
        map((response: Blob) => {
          return response;
        })
      );
  }

  getDataEquipmentTelemetryFiles(
    type: string,
    subtype: string,
    payload: any,
    format: string,
    compression: boolean
  ): Observable<Blob> {
    return this.http
      .post(
        `${this.apiBaseUrl}api/private/v1/export/equipmentTelemetryFiles?type=${type}&subtype=${subtype}&format=${format.toLowerCase()}&compression=${compression}`,
        JSON.stringify(payload),
        { headers: this.headers, responseType: 'blob' }
      )
      .pipe(
        map((response: Blob) => {
          return response;
        })
      );
  }

  buildExportPdfFaLabel(
    list: FieldActivityGrouped[],
    company: Company,
    repeatingMap: Record<string, number>,
    short: boolean
  ) {
    const dd: any = {
      pageMargins: [15, 15],
      content: [
        {
          columns: [[], []],
          columnGap: 0,
        },
      ],
    };

    let columnIndex = 1;

    for (const fa of list) {
      const repeatCount = repeatingMap[fa.ids.join('_')] || 1;

      for (let i = 0; i < repeatCount; i++) {
        const label: any = {
          width: '50%',
          table: {
            widths: ['40%', '60%'],
            headerRows: 0,
            body: this.getBodyBuildExportPdfFaLabel(fa, company, short),
          },
        };

        if (short) {
          // reset columnIndex every 4 labels
          if (columnIndex === 5) {
            columnIndex = 1;
          }

          // apply page break on 2nd and 4th label
          if (columnIndex === 2 || columnIndex === 4) {
            label.pageBreak = 'after';
          }

          const column = columnIndex < 3 ? 0 : 1;
          dd.content[0].columns[column].push(label);
        } else {
          // reset columnIndex every 2 labels
          if (columnIndex === 3) {
            columnIndex = 1;
          }

          label.pageBreak = 'after';
          const column = columnIndex === 1 ? 0 : 1;
          dd.content[0].columns[column].push(label);
        }

        columnIndex++;
      }
    }

    return dd;
  }

  getBodyBuildExportPdfFaLabel(fa: FieldActivityGrouped, company: Company, short = true) {
    const translatedCommodityName = fa.commodityNames.map((c) =>
      this.translate.instant('select_commodity.' + c, {})
    );
    const resultBody = [];

    resultBody.push([
      {
        text: company.name,
        colSpan: 2,
        alignment: 'center',
        style: { bold: true, fontSize: 14 },
        margin: [5, 5],
      },
      {},
    ]);
    if (!short) {
      resultBody.push([
        { text: 'ID', alignment: 'right', style: { bold: true, fontSize: 12 }, margin: [5, 5] },
        {
          text: fa && fa.ids && fa.ids.length > 0 ? fa.ids.join() : '',
          alignment: 'left',
          style: { bold: false, fontSize: 9 },
          margin: [5, 5],
        },
      ]);
      resultBody.push([
        {
          text: this.translate.instant('label_fa_multiple.total', {}),
          alignment: 'right',
          style: { bold: true, fontSize: 12 },
          margin: [5, 5],
        },
        {
          text: fa.cropTotalSize.toFixed(2).toString(),
          alignment: 'left',
          style: { bold: false, fontSize: 9 },
          margin: [5, 5],
        },
      ]);
    }
    resultBody.push([
      {
        text: this.translate.instant('activity.table_detail_activity.column1', {}),
        alignment: 'right',
        style: { bold: true, fontSize: 12 },
        margin: [5, 5],
      },
      {
        text:
          moment(fa.activityDate.toString()).format('DD-MM-YYYY') +
          ' (' +
          this.translate.instant('select_activity_2.' + fa.type, {}) +
          ')',
        alignment: 'left',
        style: { bold: false, fontSize: 9 },
        margin: [5, 5],
      },
    ]);
    resultBody.push([
      {
        text: this.translate.instant('form.label.crop', {}),
        alignment: 'right',
        style: { bold: true, fontSize: 12 },
        margin: [5, 5],
      },
      {
        text:
          translatedCommodityName && translatedCommodityName.length > 0
            ? translatedCommodityName.join()
            : '',
        alignment: 'left',
        style: { bold: false, fontSize: 9 },
        margin: [5, 5],
      },
    ]);
    resultBody.push([
      {
        text: this.translate.instant('batchs.table.name', {}),
        alignment: 'right',
        style: { bold: true, fontSize: 12 },
        margin: [5, 5],
      },
      {
        text: fa && fa.batchNames && fa.batchNames.length > 0 ? fa.batchNames.join() : '',
        alignment: 'left',
        style: { bold: false, fontSize: 9 },
        margin: [5, 5],
      },
    ]);
    resultBody.push([
      {
        text: this.translate.instant('item_select_viewType.groups', {}),
        alignment: 'right',
        style: { bold: true, fontSize: 12 },
        margin: [5, 5],
      },
      {
        text: fa && fa.groupNames && fa.groupNames.length > 0 ? fa.groupNames.join() : '',
        alignment: 'left',
        style: { bold: false, fontSize: 9 },
        margin: [5, 5],
      },
    ]);
    resultBody.push([
      {
        text: this.translate.instant('fieldlist.titlesmall', {}),
        alignment: 'right',
        style: { bold: true, fontSize: 12 },
        margin: [5, 5],
      },
      {
        text: fa && fa.fieldNames && fa.fieldNames.length > 0 ? fa.fieldNames.join() : '',
        alignment: 'left',
        style: { bold: false, fontSize: 9 },
        margin: [5, 5],
      },
    ]);

    if (!short) {
      resultBody.push([
        {
          text: this.translate.instant('activity.table_detail_activity.column5', {}),
          alignment: 'right',
          style: { bold: true, fontSize: 12 },
          margin: [5, 5],
        },
        {
          text: fa && fa.userNames && fa.userNames.length > 0 ? fa.userNames.join() : '',
          alignment: 'left',
          style: { bold: false, fontSize: 9 },
          margin: [5, 5],
        },
      ]);
      resultBody.push([
        {
          text: this.translate.instant('activity.table_detail_activity.column4', {}),
          alignment: 'right',
          style: { bold: true, fontSize: 12 },
          margin: [5, 5],
        },
        {
          text: fa && fa.productNames && fa.productNames.length > 0 ? fa.productNames.join() : '',
          alignment: 'left',
          style: { bold: false, fontSize: 9 },
          margin: [5, 5],
        },
      ]);
      resultBody.push([
        {
          text: this.translate.instant('activity.table_detail_activity.column9', {}),
          alignment: 'right',
          style: { bold: true, fontSize: 12 },
          margin: [5, 5],
        },
        {
          text: fa && fa.tractorNames && fa.tractorNames.length > 0 ? fa.tractorNames.join() : '',
          alignment: 'left',
          style: { bold: false, fontSize: 9 },
          margin: [5, 5],
        },
      ]);
      resultBody.push([
        {
          text: this.translate.instant('activity.table_detail_activity.column10', {}),
          alignment: 'right',
          style: { bold: true, fontSize: 12 },
          margin: [5, 5],
        },
        {
          text:
            fa && fa.equipmentNames && fa.equipmentNames.length > 0 ? fa.equipmentNames.join() : '',
          alignment: 'left',
          style: { bold: false, fontSize: 9 },
          margin: [5, 5],
        },
      ]);
    }
    resultBody.push([
      {
        colSpan: 2,
        margin: [10, 10],
        columns: [
          {
            width: '5%',
            canvas: [
              {
                type: 'polyline',
                lineWidth: 1,
                closePath: true,
                points: [
                  { x: 0, y: 0 },
                  { x: 12, y: 0 },
                  { x: 12, y: 12 },
                  { x: 0, y: 12 },
                ],
              },
            ],
            margin: [30, 0],
          },
          {
            width: '45%',
            text: { text: this.translate.instant('itemfieldtype.bio', {}), alignment: 'center' },
            style: { bold: false, fontSize: 10 },
            margin: [0, 0],
          },
          {
            width: '10%',
            canvas: [
              {
                type: 'polyline',
                lineWidth: 1,
                closePath: true,
                points: [
                  { x: 0, y: 0 },
                  { x: 12, y: 0 },
                  { x: 12, y: 12 },
                  { x: 0, y: 12 },
                ],
              },
            ],
            margin: [30, 0],
          },
          {
            width: '40%',
            text: {
              text: this.translate.instant('itemfieldtype.integrated', {}),
              alignment: 'center',
            },
            style: { bold: false, fontSize: 10 },
            margin: [0, 0],
          },
        ],
      },
      {},
    ]);
    resultBody.push([
      {
        colSpan: 2,
        columns: [
          {
            width: '50%',
            text: { text: 'Metodo di produzione Integrato', alignment: 'center' },
            style: { bold: false, fontSize: 10 },
            margin: [5, 5],
          },
          {
            width: '50%',
            text: {
              text: this.translate.instant('export.select_type.globalgap', {}),
              alignment: 'center',
            },
            style: { bold: false, fontSize: 10 },
            margin: [5, 5],
          },
        ],
      },
      {},
    ]);

    return resultBody;
  }

  getAllExportType = (): Array<any> => {
    return [
      {
        type: 'FieldActivities',
        subtype: 'CompleteCultivationPlan',
        isEnabled: true,
        mode: [UiMode.FARM, UiMode.LIVESTOCK],
        formats: [
          {
            type: 'xls',
            enabled: true,
            compression: false,
            col: '6',
          },
          {
            type: 'pdf',
            enabled: true,
            compression: false,
            col: '6',
          },
        ],
      },
      {
        type: 'FieldActivities',
        subtype: 'Treatments',
        isEnabled: true,
        mode: [UiMode.FARM, UiMode.LIVESTOCK],
        formats: [
          {
            type: 'xls',
            enabled: true,
            compression: false,
            col: '6',
          },
          {
            type: 'pdf',
            enabled: true,
            compression: false,
            col: '6',
          },
        ],
      },
      {
        type: 'FieldActivities',
        subtype: 'CultivationPlan',
        isEnabled: true,
        mode: [UiMode.FARM, UiMode.LIVESTOCK],
        formats: [
          {
            type: 'xls',
            enabled: true,
            compression: false,
            col: '6',
          },
          {
            type: 'pdf',
            enabled: true,
            compression: false,
            col: '6',
          },
        ],
      },
      {
        type: 'FieldActivities',
        subtype: 'PlotsAndParcels',
        isEnabled: true,
        mode: [UiMode.FARM, UiMode.LIVESTOCK],
        formats: [
          {
            type: 'xls',
            enabled: true,
            compression: false,
            col: '6',
          },
          {
            type: 'pdf',
            enabled: true,
            compression: false,
            col: '6',
          },
        ],
      },
      {
        type: 'FieldActivityList',
        subtype: 'FieldActivityList',
        isEnabled: this.checkActionActivity.read,
        mode: [UiMode.FARM, UiMode.LIVESTOCK],
        formats: [
          {
            type: 'csv',
            enabled: true,
            compression: false,
            col: '6',
          },
          {
            type: 'xls',
            enabled: true,
            compression: false,
            col: '6',
          },
        ],
      },
      {
        type: 'Containers',
        subtype: 'Containers',
        isEnabled: this.checkActionWarehouse.read,
        mode: [UiMode.FARM, UiMode.LIVESTOCK],
        formats: [
          {
            type: 'xls',
            enabled: true,
            compression: false,
            col: '6',
          },
          {
            type: 'pdf',
            enabled: true,
            compression: false,
            col: '6',
          },
        ],
      },
      {
        type: 'Silos',
        subtype: 'Silos',
        isEnabled: this.checkActionSilos.read,
        mode: [UiMode.LIVESTOCK],
        formats: [
          {
            type: 'xls',
            enabled: true,
            compression: false,
            col: '6',
          },
          {
            type: 'pdf',
            enabled: true,
            compression: false,
            col: '6',
          },
        ],
      },
      {
        type: 'Containers',
        subtype: 'ContainerActivities',
        isEnabled: this.checkActionWarehouse.read,
        mode: [UiMode.FARM, UiMode.LIVESTOCK],
        formats: [
          {
            type: 'xls',
            enabled: true,
            compression: false,
            col: '6',
          },
          {
            type: 'pdf',
            enabled: true,
            compression: false,
            col: '6',
          },
        ],
      },
      {
        type: 'Devices',
        subtype: 'Device',
        isEnabled: this.checkActionSensor.read,
        mode: [UiMode.FARM, UiMode.LIVESTOCK],
        formats: [
          {
            type: 'xls',
            enabled: true,
            compression: false,
            col: '12',
          },
        ],
      },
      {
        type: 'Equipments',
        subtype: 'telemetry_machine',
        subtypeExport: '',
        isEnabled: this.checkActionTelemetry.read,
        mode: [UiMode.FARM, UiMode.LIVESTOCK],
        formats: [],
      },
      {
        type: 'Bio',
        subtype: 'Bio',
        isEnabled: true,
        isPro: true,
        isProEnabled: this.checkActionExportBio.read,
        mode: [UiMode.FARM, UiMode.LIVESTOCK],
        formats: [
          {
            type: 'xls',
            enabled: true,
            compression: false,
            col: '6',
          },
          {
            type: 'pdf',
            enabled: true,
            compression: false,
            col: '6',
          },
        ],
      },
      {
        type: 'GlobalGap',
        subtype: 'GlobalGap',
        isEnabled: true,
        isPro: true,
        isProEnabled: this.checkActionExportGlobalGap.read,
        mode: [UiMode.FARM, UiMode.LIVESTOCK],
        formats: [
          {
            type: 'xls',
            enabled: true,
            compression: false,
            col: '6',
          },
          {
            type: 'pdf',
            enabled: true,
            compression: false,
            col: '6',
          },
        ],
      },
      {
        type: 'CulturalMargins',
        subtype: 'Metrics',
        isEnabled: true,
        isPro: true,
        isProEnabled: this.checkActionFinance.read,
        mode: [UiMode.FARM],
        formats: [
          {
            type: 'xls',
            enabled: true,
            compression: false,
            col: '6',
          },
          {
            type: 'pdf',
            enabled: true,
            compression: false,
            col: '6',
          },
        ],
      },
      {
        type: 'RationsPlan',
        subtype: 'RationsPlan',
        isEnabled: true,
        isPro: true,
        isProEnabled: this.checkActionRations.read,
        mode: [UiMode.LIVESTOCK],
        formats: [
          {
            type: 'xls',
            enabled: true,
            compression: false,
            col: '6',
          },
          {
            type: 'pdf',
            enabled: true,
            compression: false,
            col: '6',
          },
        ],
      },
    ];
  };
}
